/**
 * Browser-local persistence for PositionTracker's four filter maps: positions
 * sport, positions market-kind, orders sport, orders market-kind. Keeps them
 * across reload and route-away/route-back (the maps otherwise live only in
 * component-local state and reset on remount — issue #83).
 *
 * All four ride in ONE object under ONE key. Each map serializes as an array of
 * [key, mode] entries (Map has no JSON form) and is rebuilt on read. Restore is
 * defensive per the issue: unknown/obsolete/malformed ENTRIES are dropped
 * individually (a since-removed sport doesn't wipe your other valid filters),
 * and a corrupt or wrong-shaped blob falls back to all-empty (= no filter).
 *
 * The `_v1` key suffix is an escape hatch: a future breaking change to the
 * serialized shape bumps to `_v2` and cleanly orphans old data with no
 * migration code. Enum drift needs no version — it's absorbed by validation.
 */
import { SPORT_TABS, type Sport } from '@/views/ValueDashboard/types';
import type { SportFilter, SportFilterMode } from '@/lib/kalshiTicker/sportFilter';
import { storageKey } from '@/lib/infra/storage';
import { MARKET_KIND_CHIPS, type MarketKind, type MarketKindFilter } from './marketKind';

/** Exported so tests can seed raw/corrupt values under the exact key. */
export const STORAGE_KEY = storageKey('g1mt_tracker_filters_v1');

export interface TrackerFilters {
  posSport: SportFilter;
  posKind: MarketKindFilter;
  ordSport: SportFilter;
  ordKind: MarketKindFilter;
}

const isValidSport = (k: string): k is Sport => SPORT_TABS.some((t) => t.id === k);
const isValidKind = (k: string): k is MarketKind => MARKET_KIND_CHIPS.some((c) => c.id === k);
const isValidMode = (m: unknown): m is SportFilterMode => m === 'include' || m === 'exclude';

function emptyFilters(): TrackerFilters {
  return { posSport: new Map(), posKind: new Map(), ordSport: new Map(), ordKind: new Map() };
}

/**
 * Rebuild one filter map from a stored value, dropping any entry that isn't a
 * [validKey, validMode] pair. A non-array (missing/garbage field) yields an
 * empty map without disturbing the other three.
 */
function reviveMap<K extends string>(
  raw: unknown,
  isValidKey: (k: string) => k is K,
): ReadonlyMap<K, SportFilterMode> {
  const out = new Map<K, SportFilterMode>();
  if (!Array.isArray(raw)) return out;
  for (const entry of raw) {
    if (!Array.isArray(entry) || entry.length !== 2) continue;
    const [key, mode] = entry;
    if (typeof key === 'string' && isValidKey(key) && isValidMode(mode)) {
      out.set(key, mode); // duplicate keys collapse, last-wins
    }
  }
  return out;
}

function serializeMap<K extends string>(
  map: ReadonlyMap<K, SportFilterMode>,
): Array<[K, SportFilterMode]> {
  return Array.from(map.entries());
}

/** Read + validate the four maps. Never throws; corrupt storage → no filter. */
export function readTrackerFilters(): TrackerFilters {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyFilters();
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return emptyFilters();
    const o = parsed as Record<string, unknown>;
    return {
      posSport: reviveMap(o.posSport, isValidSport),
      posKind: reviveMap(o.posKind, isValidKind),
      ordSport: reviveMap(o.ordSport, isValidSport),
      ordKind: reviveMap(o.ordKind, isValidKind),
    };
  } catch {
    return emptyFilters();
  }
}

/** Persist all four maps. Never throws; a quota/availability failure is a no-op. */
export function writeTrackerFilters(filters: TrackerFilters): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        posSport: serializeMap(filters.posSport),
        posKind: serializeMap(filters.posKind),
        ordSport: serializeMap(filters.ordSport),
        ordKind: serializeMap(filters.ordKind),
      }),
    );
  } catch {
    // localStorage unavailable or over quota — dropping the write is acceptable
    // for a display-only preference.
  }
}
